import { XIcon } from '@heroicons/react/solid';
import { Input, Modal } from 'antd';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { getLoginToken } from '../../shared/getToken';
import { notify } from '../../shared/notify';
import { handleEmailOnBlur } from '../../V2/SignUpModal';
import { user } from '../../../atoms/user';
import { contactQuery } from '../../../services/ContactUs';
import { contactQueryPayload } from '../../../utils/interfaces/contactUs';
import { User } from '../../../utils/interfaces/User';

interface Props {
  visible: { value: boolean; type: string };
  onCancel: () => void;
}

const ContactForm: React.FC<Props> = ({ visible, onCancel }) => {
  const [loading, setLoading] = useState({ formType: '', value: false });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleCancel = () => {
    setErrors({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
    });
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
    });
    onCancel();
  };
  const handleOk = () => {
    setConfirmLoading(true);

    setTimeout(() => {
      setConfirmLoading(false);
      handleCancel();
    }, 2000);
  };

  const userData: User | undefined = useRecoilValue(user);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = await getLoginToken();

    const validationErrors = {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      message: '',
    };

    if (!formData.first_name) {
      validationErrors.first_name = 'First name is required';
    }

    if (!formData.last_name) {
      validationErrors.last_name = 'Last name is required';
    }

    if (!formData.email) {
      validationErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      validationErrors.email = 'Invalid email';
    }
    if (formData.email) {
      const res = await handleEmailOnBlur(formData.email);
      validationErrors.email = res;
    }

    if (!formData.phone) {
      validationErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone)) {
      validationErrors.phone = 'Enter Valid 10 digit number';
    }
    if (!formData.message) {
      validationErrors.message = 'Message is required';
    }

    setErrors(validationErrors);

    if (Object.values(validationErrors).every((error) => !error)) {
      try {
        setLoading((prevState) => ({
          ...prevState,
          value: true,
          formType: visible?.type,
        }));
        let payload: contactQueryPayload = { ...formData, type: visible?.type };
        if (token) {
          payload = { ...payload, user_id: userData?.id };
        }

        await contactQuery(payload);
        notify(
          'Your inquiry has been successfully submitted. Our team will respond to you within 24 hours.',
          'success',
        );
        handleCancel();
      } catch (error: any) {
        const message = formateErrorObject(error);
        notify(message, 'error');
      } finally {
        setLoading((prevState) => ({
          ...prevState,
          value: false,
        }));
      }
    }
  };

  const isValidPhone = (value: string) => {
    const phoneRegex = /^\d{10}$/;

    return phoneRegex.test(value);
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  return (
    <Modal
      title=''
      open={visible.value}
      centered={true}
      onOk={handleOk}
      closable={false}
      confirmLoading={confirmLoading}
      onCancel={handleCancel}
      footer={false}
      width={800}
      className='customLoginModal bg-bgColor bg-opacity-70   rounded-2xl border-2 border-borderDarkGray backdrop-filter backdrop-blur-md '
    >
      <div className='text-fadedWhite text-xl'>
        <div className='flex justify-between items-center  bg-backGroundReaderTabBg rounded-t-2xl px-4 md:px-8 py-6'>
          <div className='flex flex-col gap-1'>
            <span>
              {visible.type === 'FEEDBACK'
                ? 'Feedback / Suggestions'
                : 'Business Inquiries / Partnerships'}{' '}
            </span>
            <span className='text-textGray text-sm '>Send Inquiries</span>
          </div>

          <span className='border rounded-full p-1.5 border-[#363636] flex justify-center items-center'>
            <XIcon
              onClick={() => handleCancel()}
              className='w-5 h-5  text-[#A6A6A6] cursor-pointer'
            />
          </span>
        </div>

        <form
          onSubmit={handleSubmit}
          className='grid grid-cols-2 gap-4 pt-8 px-4 md:px-8'
        >
          <div className='col-span-2 xs:col-span-1 flex-col flex gap-y-.5'>
            <Input
              name='first_name'
              size='large'
              value={formData.first_name}
              style={{
                backgroundColor: '#141414',
              }}
              placeholder='First Name'
              onChange={handleChange}
              className='border text-base placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray customInput py-3  text-textDarkGrayColor bg-backgroundGray'
            />
            {errors.first_name && (
              <p className='text-red-500 text-sm mt-1'>{errors.first_name}</p>
            )}
          </div>
          <div className='col-span-2 xs:col-span-1 flex-col flex gap-y-.5'>
            <Input
              name='last_name'
              style={{
                backgroundColor: '#141414',
              }}
              placeholder='Last Name'
              value={formData.last_name}
              onChange={handleChange}
              size='large'
              className='border text-base placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray customInput py-3  text-textDarkGrayColor bg-backgroundGray'
            />
            {errors.last_name && (
              <p className='text-red-500 text-sm mt-1'>{errors.last_name}</p>
            )}
          </div>
          <div className='col-span-2 md:col-span-1 flex-col flex gap-y-.5'>
            <Input
              name='email'
              type='email'
              autoComplete='current-email'
              value={formData.email}
              style={{
                backgroundColor: '#141414',
              }}
              placeholder='Email'
              onChange={handleChange}
              size='large'
              className='border text-base placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray customInput py-3  text-textDarkGrayColor bg-backgroundGray'
            />
            {errors.email && (
              <p className='text-red-500 text-sm mt-1'>{errors.email}</p>
            )}
          </div>
          <div className='col-span-2 md:col-span-1 flex-col flex gap-y-.5'>
            <Input
              name='phone'
              value={formData.phone}
              inputMode='numeric'
              type='tel'
              autoComplete='tel'
              style={{
                backgroundColor: '#141414',
              }}
              placeholder='Phone Number'
              onChange={handleChange}
              size='large'
              className='border text-base placeholder:text-textDarkGrayColor rounded-xl border-bgDarkGray customInput py-3  text-textDarkGrayColor bg-backgroundGray'
            />
            {errors.phone && (
              <p className='text-red-500 text-sm mt-1'>{errors.phone}</p>
            )}
          </div>
          <div className='col-span-2 border bg-[#000000] rounded-xl border-bgDarkGray  px-3 pt-5 flex-col flex gap-y-.5'>
            <span className='text-white text-sm  pb-2'>Write your Message</span>
            <Input.TextArea
              name='message'
              value={formData.message}
              style={{
                backgroundColor: '#141414',
              }}
              placeholder='Describe your inquiries'
              onChange={handleChange}
              rows={10}
              size='large'
              className='border-none focus:ring-0 text-sm placeholder:text-textDarkGrayColor rounded-xl px-1 border-bgDarkGray customInput py-2  text-textDarkGrayColor bg-backgroundGray'
            />
          </div>
          {/* <div className='col-span-2'>
        <Checkbox
          name='acceptTerms'
          checked={formData.acceptTerms}
          onChange={(e) => handleCheckboxChange(e.target.checked)}
        >
          I accept the terms
        </Checkbox>
        {errors.acceptTerms && (
          <p className='text-red-500'>{errors.acceptTerms}</p>
        )}
      </div> */}
          <div className='col-span-2'>
            <div className='flex gap-x-5'>
              <ButtonDefault
                size={4}
                rounded={true}
                className='w-1/3'
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleCancel();
                }}
                variant={ButtonVariants.GRAY}
              >
                <span className='flex text-sm gap-x-2 text-gray-400 px-4 py-1 whitespace-nowrap'>
                  Cancel
                </span>
              </ButtonDefault>
              <ButtonDefault
                size={4}
                loading={loading.value}
                rounded={true}
                disabled={loading.value}
                className='w-2/3'
                variant={ButtonVariants.WHITE}
              >
                <span className='flex text-sm gap-x-2 px-4 py-1 whitespace-nowrap'>
                  Submit
                </span>
              </ButtonDefault>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ContactForm;
